import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { HF } from "./homeFeedUi";
import { FreeMemberHeaderActions } from "./viewer";

/**
 * Chrome for the account pages a signed-in free member keeps (Settings, help, legal,
 * blog, community wins). Free members live on the public deals site, so this is that
 * site's header (logo, Back to deals, Upgrade, account) instead of the members app's
 * sidebar and bottom tabs.
 */
export default function FreeAccountShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  // Settings and Community wins own their scrolling (full-height layouts), as in the members app.
  const fillsViewport = location.pathname === "/community-successes" || location.pathname === "/settings";

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50 dark:bg-dark-950" data-testid="free-account-shell">
      <header className="flex-shrink-0 border-b text-white" style={{ background: HF.header, borderColor: "var(--hf-header-border)", fontFamily: HF.font }}>
        <div className="mx-auto flex h-14 max-w-[1360px] items-center gap-1.5 px-4 sm:h-16 sm:gap-3 sm:px-5">
          <Link to="/" aria-label="DealHawk home" className="flex flex-shrink-0 items-center gap-2 text-white no-underline">
            <img src="/hawk_icon_white.svg" alt="" width={28} height={28} className="h-[26px] w-[26px] object-contain sm:h-[28px] sm:w-[28px]" />
            <span className="hidden text-[18px] font-extrabold tracking-[-0.4px] sm:inline">DealHawk</span>
          </Link>
          <Link
            to="/"
            className="ml-1 flex h-9 flex-shrink-0 items-center gap-1 rounded-full px-2.5 text-[13.5px] font-semibold no-underline transition-colors hover:bg-white/10 sm:ml-3 sm:px-3 sm:text-sm"
            style={{ color: "var(--hf-header-muted)" }}
            data-testid="back-to-deals"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
            Back to deals
          </Link>
          <span className="flex-1" />
          <FreeMemberHeaderActions source="account" />
        </div>
      </header>
      <main
        data-route-scroll
        className={fillsViewport ? "flex min-h-0 flex-1 flex-col overflow-hidden" : "min-h-0 flex-1 overflow-y-auto overflow-x-hidden"}
      >
        <div className={fillsViewport ? "flex min-h-0 w-full flex-1 flex-col" : "mx-auto max-w-8xl"}>{children}</div>
      </main>
    </div>
  );
}
